import type { ShowPersonioSetupRowsResponse } from '@bliks-io/contracts';
import {
  createMembershipRowSchema,
  createOrgFunctionRowSchema,
  type ParsedMembershipRow,
  type ParsedOrgFunctionRow
} from '@/lib/excel';
import { zodRowValidator, type RowValidator } from '@/lib/draft-rows/draftRow';
import { validateDepartmentRow, validateUserRow } from './draftRowValidation';
import type { ParsedDepartment, ParsedUser } from './excelParsers';

/**
 * The data-sync setup rows as the draft rows an Excel upload yields:
 * validated with the upload's schemas and numbered from 1.
 */
function toDraftRows<TData>(rows: TData[], validateRow: RowValidator<TData>) {
  return rows.map((data, index) => ({ data, ...validateRow(data), rowNumber: index + 1 }));
}

export function setupDepartmentDrafts(
  departments: ShowPersonioSetupRowsResponse['departments']
): ParsedDepartment[] {
  return toDraftRows(
    departments.map((department) => ({
      ...department,
      managerEmail: department.managerEmail ?? ''
    })),
    validateDepartmentRow
  );
}

export function setupUserDrafts(users: ShowPersonioSetupRowsResponse['users']): ParsedUser[] {
  return toDraftRows(users, validateUserRow);
}

export function setupTeamDrafts(
  setupRows: Pick<ShowPersonioSetupRowsResponse, 'teams' | 'memberships'>
): {
  rows: ParsedOrgFunctionRow[];
  memberships: ParsedMembershipRow[];
} {
  return {
    rows: toDraftRows(setupRows.teams, zodRowValidator(createOrgFunctionRowSchema)),
    memberships: toDraftRows(setupRows.memberships, zodRowValidator(createMembershipRowSchema))
  };
}
